import React, { useEffect, useState } from "react";

import { flatMap, isEmpty, uniq } from "lodash/fp";
import {
  useDropzone,
  ErrorCode as FileErrorCode,
  FileError,
} from "react-dropzone";

import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Stack, Typography } from "@mui/material";

import SettingAlert from "@app/analytics/account/components/SettingAlert";
import { formattedListWithConjunction } from "@app/common/utils";
import { UploadLogoBody } from "@app/common/workspaces/WorkspaceContext";

const MAX_FILE_MB = 2;
const ACCEPTED_FILE_TYPES = [".png", ".jpeg"];

const toBytes = (mb: number) => mb * 1000000;

const getUniqueIdentifier = () =>
  `${Math.round(Math.random() * Math.random() * 100000000)}`;

const toFriendlyErrorMessage = (error: FileError) => {
  switch (error.code) {
    case FileErrorCode.FileTooLarge:
      return `File may not be larger than ${MAX_FILE_MB}MB`;
    case FileErrorCode.FileInvalidType:
      return `File type must be ${formattedListWithConjunction(
        ACCEPTED_FILE_TYPES,
        "or"
      )}`;
    default:
      return error.message;
  }
};

const LogoUploaderInput = ({
  onUpload,
  uploading,
}: {
  onUpload: (file: File) => void;
  uploading: boolean;
}) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    accept: {
      "image/*": ACCEPTED_FILE_TYPES,
    },
    maxSize: toBytes(MAX_FILE_MB),
    multiple: false,
  });

  const handleClickUpload = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isEmpty(acceptedFiles)) return;
    return onUpload(acceptedFiles[0]);
  };

  const fileRejectionErrors = uniq(flatMap("errors", fileRejections));
  const loadedFile = acceptedFiles[0];

  return (
    <>
      <SettingAlert show={!isEmpty(fileRejections)} severity="error" persist>
        Oops! There was a problem uploading your logo:
        {fileRejectionErrors.map((error) => (
          <li key={error.code}>{toFriendlyErrorMessage(error)}</li>
        ))}
      </SettingAlert>
      <Stack
        {...getRootProps()}
        sx={{
          border: (theme) => `1px dashed ${theme.palette.shades.neutral[300]}`,
          borderColor: isDragActive
            ? "shades.primary.300"
            : "shades.neutral.300",
          bgcolor: isDragActive ? "shades.ghost.300" : "background.default",
          borderRadius: "4px",
          width: "100%",
          maxWidth: "400px",
          "&:hover": {
            borderColor: "shades.primary.300",
            bgcolor: "shades.ghost.300",
            cursor: "pointer",
          },
          padding: "14px 8px 24px 8px",
        }}
        alignItems="center"
        justifyContent="center"
        spacing="10px"
      >
        <input {...getInputProps()} />
        <Typography variant="bodySecondaryReg">
          Drag and drop image or{" "}
          <Typography
            variant="bodySecondaryLink"
            sx={{ color: "shades.primary.500" }}
          >
            browse files
          </Typography>
        </Typography>
        {loadedFile ? (
          <>
            <Typography
              variant="bodyPrimaryMed"
              sx={{
                maxWidth: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                lineWrap: "nowrap",
                padding: "0 8px",
              }}
            >
              {loadedFile.name}
            </Typography>
            <LoadingButton
              loading={uploading}
              onClick={handleClickUpload}
              sx={{ height: "48px" }}
              variant="contained"
            >
              Upload & Save
            </LoadingButton>
          </>
        ) : (
          <Stack alignItems="center">
            <Typography variant="labelReg" sx={{ color: "shades.neutral.600" }}>
              Max file size: {MAX_FILE_MB}MB
            </Typography>
            <Typography variant="labelReg" sx={{ color: "shades.neutral.600" }}>
              {formattedListWithConjunction(ACCEPTED_FILE_TYPES, "or")} accepted
            </Typography>
          </Stack>
        )}
      </Stack>
    </>
  );
};

const LogoUploader = ({
  logoUri,
  onUpload,
  onDelete,
}: {
  logoUri?: string;
  onUpload: (opts: UploadLogoBody) => void;
  onDelete: () => void;
}) => {
  const [changingImage, setChangingImage] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [deletingImage, setDeletingImage] = useState<boolean>(false);

  useEffect(() => {
    if (logoUri == null) {
      setDeletingImage(false);
    }
  }, [logoUri]);

  // the logo uri won't change when the image is updated since we are proxying the
  // s3 path through the BE; this ensures that the image sources changes on a
  // successful upload, preventing the need for the user to force-refresh the page
  // to see their newly uploaded image
  const [uniqueLogoIdentifier, setUniqueLogoIdentifier] = useState<string>(
    getUniqueIdentifier()
  );

  const handleCancelChangeImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setChangingImage(false);
  };

  const showUploadInput = !logoUri || changingImage;

  const handleDeleteImage = () => {
    setDeletingImage(true);
    onDelete();
  };

  const handleClickUpload = async (file: File) => {
    setUploading(true);

    return onUpload({
      file,
      onSuccess: () => {
        setUploading(false);
        setChangingImage(false);
        setUniqueLogoIdentifier(getUniqueIdentifier());
      },
    });
  };

  return (
    <Stack>
      {logoUri && (
        <Stack spacing={2} sx={{ mb: showUploadInput ? 2 : 0 }}>
          <Stack direction="row" sx={{ height: "48px", minWidth: "48px" }}>
            <Box
              component="img"
              src={`${logoUri}?${uniqueLogoIdentifier}`}
              sx={{ height: "100%" }}
            />
          </Stack>
          {changingImage ? (
            <Button
              variant="outlined"
              onClick={handleCancelChangeImage}
              sx={{
                width: "120px",
                borderWidth: "2px",
                "&:hover": { borderWidth: "2px" },
              }}
            >
              Cancel
            </Button>
          ) : (
            <Stack direction="row" spacing={2} sx={{ height: "48px" }}>
              <Button
                variant="contained"
                sx={{ width: "140px" }}
                onClick={() => setChangingImage(true)}
              >
                Change Image
              </Button>
              <LoadingButton
                variant="outlined"
                color="error"
                sx={{ width: "140px" }}
                onClick={handleDeleteImage}
                loading={deletingImage}
              >
                Delete Image
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      )}
      {showUploadInput && (
        <LogoUploaderInput onUpload={handleClickUpload} uploading={uploading} />
      )}
    </Stack>
  );
};

export default LogoUploader;
